import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { USER_TYPE_LABELS, User } from 'app/core/user/user.types';
import { NotificationService } from 'app/core/utils/notification.service';

/**
 * Red strip above the admin header while an admin-parent is impersonating
 * another admin (teacher / operator) inside this app. Visibility comes from
 * the session token's "act" claim, so it survives reloads and new tabs; the
 * button calls /auth/stop-impersonating and reloads as the admin-parent.
 */
@Component({
    selector: 'impersonation-banner',
    imports: [AsyncPipe, MatIconModule],
    template: `
        @if (visible) {
            <div
                role="status"
                class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 bg-red-600 px-4 py-2 text-white print:hidden"
            >
                <div class="flex min-w-0 flex-1 items-center gap-2">
                    <mat-icon
                        class="icon-size-5 shrink-0 text-white"
                        [svgIcon]="'heroicons_outline:eye'"
                    ></mat-icon>
                    <span class="truncate text-sm font-semibold">
                        Acționezi ca {{ label(user$ | async) }}. Modificările vor
                        afecta contul acestui utilizator.
                    </span>
                </div>
                <button
                    type="button"
                    class="shrink-0 rounded bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-60"
                    [disabled]="busy"
                    (click)="stop()"
                >
                    Ieși din impersonare
                </button>
            </div>
        }
    `,
})
export class ImpersonationBannerComponent {
    private _auth = inject(AuthService);
    private _notification = inject(NotificationService);

    user$ = inject(UserService).user$;
    busy = false;

    get visible(): boolean {
        return this._auth.isImpersonating;
    }

    label(user: User | null): string {
        if (!user) return '';
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        const type = USER_TYPE_LABELS[user.type] ?? user.type;
        return `${name || user.email} (${user.email}, ${type})`;
    }

    stop(): void {
        if (this.busy) return;
        this.busy = true;
        this._auth.stopImpersonating().subscribe({
            error: () => {
                this.busy = false;
                this._notification.error('Ieșirea din impersonare a eșuat.');
            },
        });
    }
}
